import { Order } from "@/types/ApiTypes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColumnId } from "./DriverOrdersBoard";
import DriverModal from "./DriverModal";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";
import { ChevronDownIcon, MapPinIcon } from "lucide-react";
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';

interface Props {
  order: Order;
  columnId?: ColumnId;
  onStatusChange: (orderId: string, newStatus: ColumnId) => void;
}

export default function DriverOrderCard({
  order,
  columnId,
  onStatusChange,
}: Props) {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  useLockBodyScroll(modalOpen);

  function toggleModal() {
    setModalOpen(!modalOpen);
  }

  const [driverPoint, setDriverPoint] = useState<google.maps.LatLngLiteral | null>(null);

  // Check if running on mobile platform
  const isMobile = Capacitor.isNativePlatform();

  useEffect(() => {
    if (isMobile) {
      // Use Capacitor Geolocation for mobile
      Geolocation.getCurrentPosition({ enableHighAccuracy: true })
        .then((position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setDriverPoint(coords);
        })
        .catch((err) => {
          console.error("Capacitor Geolocation error", err);
          setAlertModalOpen(true);
        });
    } else if (navigator.geolocation) {
      // Use browser geolocation for web
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          setDriverPoint(coords);
        },
        (err) => {
          console.error("Browser Geolocation error", err);
          setAlertModalOpen(true);
        },
        { enableHighAccuracy: true }
      );
    } else {
      console.error("Geolocation not supported");
      setAlertModalOpen(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (driverPoint === null) {
      setAlertModalOpen(true);
    } else {
      setAlertModalOpen(false);
    }
  }, [driverPoint]);

  const getAvailableStatuses = (currentStatus?: ColumnId): ColumnId[] => {
    if (!currentStatus) return [];
    switch (currentStatus) {
      case "finished":
        return ["finished", "delivering"];
      case "delivering":
        return ["delivering", "delivered"];
      case "delivered":
        return ["delivered"];
      default:
        return [currentStatus];
    }
  };

  const handleStatusChange = (newStatus: ColumnId) => {
    if (order._id && columnId && newStatus !== columnId) {
      onStatusChange(order._id, newStatus);
    }
  };

  const getLocation = () => {
    if (isMobile) {
      // Use Capacitor Geolocation for mobile
      Geolocation.getCurrentPosition({ enableHighAccuracy: true,timeout: 10000 })
        .then((position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          console.log("🎉🎉🎉🎉🎉🎉🎉",`coords: ${coords}`, `position: ${position}`);
          setDriverPoint(coords);
        })
        .catch((err) => {
          console.error("Capacitor Geolocation error", err);
          alert(t("locationModal.unsupported"));
        });
    } else if (navigator.geolocation) {
      // Use browser geolocation for web
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          setDriverPoint(coords);
        },
        (err) => {
          console.error("Browser Geolocation error", err);
          alert(t("locationModal.unsupported"));
        },
        { enableHighAccuracy: true }
      );
    } else {
      alert(t("locationModal.unsupported"));
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <>
      {alertModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999]">
          <div className="bg-white px-8 py-6 rounded-2xl shadow-2xl w-full max-w-md text-center space-y-5">
            <div className="flex flex-col items-center">
              <svg
                className="w-12 h-12 text-red-500 mb-2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3m0 4h.01M4.293 6.293a1 1 0 011.414 0L12 12.586l6.293-6.293a1 1 0 011.414 1.414L13.414 14l6.293 6.293a1 1 0 01-1.414 1.414L12 15.414l-6.293 6.293a1 1 0 01-1.414-1.414L10.586 14 4.293 7.707a1 1 0 010-1.414z"
                />
              </svg>
              <h2 className="text-2xl font-bold text-gray-800">{t("locationModal.title")}</h2>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              {t("locationModal.description")}
            </p>
            <div className="flex gap-2 flex-wrap md:flex-nowrap">
              <button
                onClick={getLocation}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg shadow-md"
              >
                {t("locationModal.getLocation")}
              </button>
              <button
                onClick={handleReload}
                className="w-full bg-sidebar hover:from-blue-700 text-white font-medium py-2.5 rounded-lg shadow-md"
              >
                {t("locationModal.reload")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        className={`rounded-xl border border-opacity-30 p-5 transition-all duration-300 ease-in-out
        ${columnId === "delivered"
            ? "bg-green-300 border-green-300 shadow-green-sm"
            : columnId === "delivering"
              ? "bg-blue-300 border-blue-300 shadow-blue-sm"
              : "bg-amber-300 border-amber-300 shadow-amber-sm"
          }`}
      >
        <div className="flex flex-col gap-4">
          {/* Order Header */}
          <div className="flex items-center justify-between">
            <div dir="ltr" className="flex gap-1 items-center">
              <h3 className="text-sm m-0 font-bold uppercase">
                {t("order")}
              </h3>
              <h2 className="text-sm font-bold text-gray-800">
                #{order.orderId}
              </h2>
            </div>

            {columnId && (
              <div
                className={`rounded-full px-3 py-1 text-xs font-semibold
              ${columnId === "delivered"
                  ? "bg-green-100 text-green-800"
                  : columnId === "delivering"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-amber-100 text-amber-800"
                }`}
              >
                {t(`driver.${columnId}`)}
              </div>
            )}
          </div>

          {/* Status Selector */}
          {columnId && (
            <div className="relative">
              <select
                className="w-full appearance-none rounded-lg border border-gray-200 bg-white py-2.5 pl-4 pr-10 text-sm text-gray-800 shadow-xs transition-all hover:border-gray-300 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                value={columnId}
                onChange={(e) => handleStatusChange(e.target.value as ColumnId)}
              >
                {getAvailableStatuses(columnId).map((status) => (
                  <option key={status} value={status}>
                    {t(`driver.${status}`)}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={toggleModal}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-xs transition-all hover:bg-gray-50 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200 border border-gray-200"
          >
            <MapPinIcon className="h-4 w-4 text-sky-500" />
            {t("address.details")}
          </button>
        </div>
      </div>

      {modalOpen && <DriverModal order={order} toggleModal={toggleModal} setDriverPoint={setDriverPoint} driverPoint={driverPoint} />}
    </>
  );
}